import {
  MorphCard,
  MorphOpen,
  MorphRest,
  MorphShared,
  useMorphCard,
} from '@/Components/ui/morph-card'
import { cn } from '@/lib/utils'
import type { Bundle } from '@/types'
import {
  Currency,
  PLAN_CLOSE_SPOT,
  PLAN_SHEET,
  PLAN_SURFACE,
  PLAN_TITLE,
  PlanActivate,
  PlanArrow,
  PlanBackdrop,
  PlanBadges,
  PlanCloseButton,
  PlanEdge,
  PlanFlag,
  PlanFlagMark,
  PlanLines,
  PlanRates,
  PlanRegions,
  usePlanFacts,
  type PlanFacts,
} from './plan-parts'

/** A plan or a bundle in the grid, opening to everything it carries. */
export function PlanCard({ plan }: { plan: Bundle }) {
  const facts = usePlanFacts(plan)

  return (
    <MorphCard
      id={String(plan.id)}
      label={facts.title}
      radius={22}
      className={PLAN_SURFACE}
      restClassName="h-[190px] w-full"
      openClassName="w-[380px] max-w-[calc(100%-2rem)]"
      drawerClassName={PLAN_SHEET}
    >
      <PlanContent facts={facts} />
    </MorphCard>
  )
}

/**
 * A plan's two states share less than a partner's: the title and the price.
 * Those two make the journey -- the price from the foot of the card to its
 * place under the lines, growing as it goes. The picture, the badges and the
 * validity belong to the card; the lines, the blocks and the button to the panel.
 */
function PlanContent({ facts }: { facts: PlanFacts }) {
  const { isOpen, moves, enter, close } = useMorphCard()

  return (
    <>
      <MorphRest className="absolute inset-0">
        <PlanBackdrop />
      </MorphRest>

      {/* Zero-height and stuck to the box, so a long plan scrolls under them. */}
      {moves ? (
        <MorphOpen className="sticky top-0 z-30 h-0">
          <PlanEdge at="top" />
        </MorphOpen>
      ) : null}

      {facts.country ? (
        <>
          <MorphRest className="absolute end-2 top-2 z-10">
            <PlanFlag facts={facts} />
          </MorphRest>
          <MorphOpen className="pointer-events-none absolute end-2 top-2">
            <PlanFlagMark facts={facts} />
          </MorphOpen>
        </>
      ) : null}

      <MorphOpen className={PLAN_CLOSE_SPOT}>
        <PlanCloseButton onClose={close} />
      </MorphOpen>

      <div
        className={cn(
          'relative flex flex-col',
          isOpen ? 'gap-4 p-6' : 'h-full justify-between p-4'
        )}
      >
        <div>
          <MorphShared
            as="span"
            data-piece="name"
            style={{ fontSize: facts.titleSize }}
            className={PLAN_TITLE}
          >
            {facts.title}
          </MorphShared>

          <MorphRest>
            <PlanBadges facts={facts} />
          </MorphRest>
        </div>

        <MorphOpen fade={false}>
          {(present) => <PlanLines facts={facts} shown={present} duration={enter} />}
        </MorphOpen>

        <div className="flex items-end justify-between gap-2">
          <div className={cn('flex items-baseline', isOpen && 'gap-1')}>
            <MorphShared as="span" resize data-piece="logo" className="flex shrink-0 self-center">
              <Currency />
            </MorphShared>

            <MorphShared
              as="span"
              resize
              data-piece="price"
              className={cn('block font-bold tabular-nums', isOpen ? 'text-3xl' : 'text-2xl')}
            >
              {facts.price}
            </MorphShared>

            <MorphRest className="ms-1 whitespace-nowrap text-sm">/ {facts.validity}</MorphRest>
          </div>

          <MorphRest>
            <PlanArrow />
          </MorphRest>
        </div>

        <MorphOpen className="flex flex-col gap-4">
          <PlanRegions facts={facts} />
          <PlanRates facts={facts} />
          <PlanActivate facts={facts} />
        </MorphOpen>
      </div>

      {moves ? (
        <MorphOpen className="sticky bottom-0 z-30 h-0">
          <PlanEdge at="bottom" />
        </MorphOpen>
      ) : null}
    </>
  )
}
